import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';
import './IconButton.css';

export type IconButtonSize = 's' | 'm';
export type IconButtonVariant = 'default' | 'danger';

interface IconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Иконка (один элемент, например <EditIcon size={16} />). Размер иконки при необходимости задаётся через iconSize. */
  children: ReactNode;
  /** Доступное описание кнопки (обязательно для доступности). */
  'aria-label': string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  /** Размер иконки в пикселях; передаётся в дочерний элемент, если он принимает проп size. */
  iconSize?: number;
}

const SIZE_PX: Record<IconButtonSize, number> = { s: 28, m: 32 };
const DEFAULT_ICON_SIZE: Record<IconButtonSize, number> = { s: 16, m: 18 };

export function IconButton({
  children,
  'aria-label': ariaLabel,
  variant = 'default',
  size = 'm',
  iconSize,
  className = '',
  ...props
}: IconButtonProps) {
  const boxSize = SIZE_PX[size];
  const resolvedIconSize = iconSize ?? DEFAULT_ICON_SIZE[size];
  const child =
    isValidElement(children) && typeof (children as ReactElement<{ size?: number }>).props?.size === 'undefined'
      ? cloneElement(children as ReactElement<{ size?: number }>, { size: resolvedIconSize })
      : children;

  return (
    <button
      type="button"
      className={`icon-btn icon-btn_${size} icon-btn_${variant} ${className}`.trim()}
      style={{ width: boxSize, height: boxSize, minWidth: boxSize, minHeight: boxSize }}
      aria-label={ariaLabel}
      {...props}
    >
      {child}
    </button>
  );
}
